import styled from "styled-components";
import useHomeworkClassView from "features/homework/hooks/useHomeworkClassView";

interface HomeworkClassViewProps{
    onLessonClickHandler:(id:string) => void,
    selectedLesson:string
}

const HomeworkClassView = ({selectedLesson, onLessonClickHandler}:HomeworkClassViewProps) => {
    const { lessonList, searchKeyword, sortOrder, handleSearch, toggleSortOrder, isLoading, loadingProgress } = useHomeworkClassView();

    return(
        <Container>
            <SearchContainer>
                <SearchWrapper>
                    <SearchIcon>🔍</SearchIcon>
                    <SearchInput 
                        type="text"
                        placeholder="학급명 검색..."
                        value={searchKeyword}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </SearchWrapper>
                <SortButton onClick={toggleSortOrder}>
                    {sortOrder === 'asc' ? '↑ 오름차순' : '↓ 내림차순'}
                </SortButton>
            </SearchContainer>
            <Label>클릭하여 선택하세요</Label>
            <Table>
                <TableHeader>
                    <TableColumn style={{width:"45%"}}>학급명</TableColumn>
                    <TableColumn style={{width:"35%"}}>선생님</TableColumn>
                    <TableColumn style={{width:"20%"}}>학생수</TableColumn>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        <LoadingContainer>
                            <LoadingSpinner />
                            <LoadingText>학급 목록을 불러오는 중... {Math.round(loadingProgress)}%</LoadingText>
                        </LoadingContainer>
                    ) : lessonList.length === 0 ? (
                        <EmptyHint>표시할 학급이 없습니다</EmptyHint>
                    ) : (
                        lessonList.map((item) => (
                            <TableRow 
                                key={item.lesson_id} 
                                onClick={() => onLessonClickHandler(item.lesson_id)} 
                                selected={selectedLesson === item.lesson_id}
                            >
                                <TableColumn style={{width:"45%"}}>{item.lesson_name}</TableColumn>
                                <TableColumn style={{width:"35%"}}>{item.teacher_name}</TableColumn>
                                <TableColumn style={{width:"20%"}}>{item.student_count}</TableColumn>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    animation-name: slideInFromLeft;
    animation-duration: 1s;
    width: 400px;
    flex: 1;
    min-height: 0;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.04);
    padding: 16px;
`

const Label = styled.div`
    font-size: 12px;
    color: #64748B;
    padding-left: 12px;
`

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-grow: 1;
    background: #FFFFFF;
    border-radius: 8px;
    overflow: hidden;
`

const TableHeader = styled.div`
    display: flex;
    background: #F8FAFC;
    border-bottom: 2px solid #E2E8F0;
    position: sticky;
    top: 0;
    z-index: 1;
`

const TableColumn = styled.div`
    text-align: left;
    padding: 12px 16px;
    font-size: 14px;
    color: #64748B;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TableBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #F1F5F9;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #CBD5E1;
        border-radius: 3px;
    }
`

const TableRow = styled.div<{selected?:boolean}>`
    display: flex;
    border-bottom: 1px solid #E2E8F0;
    background-color: ${(props) => props.selected ? "#FEF2F2" : "#FFFFFF"};
    box-shadow: ${(props) => props.selected ? "inset 3px 0 0 #DF0000" : "none"};
    transition: all 0.2s ease;
    cursor: pointer;
    
    &:hover {
        background-color: ${props => props.selected ? "#FEF2F2" : "#F8FAFC"};
    }
    
    ${TableColumn} {
        color: #1E293B;
        font-weight: ${(props) => props.selected ? 600 : 400};
    }
`

const EmptyHint = styled.div`
    padding: 40px 16px;
    text-align: center;
    color: #94A3B8;
    font-size: 13px;
`

const SearchContainer = styled.div`
    display: flex;
    gap: 12px;
`

const SearchWrapper = styled.div`
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
`

const SearchIcon = styled.span`
    position: absolute;
    left: 12px;
    color: #94A3B8;
    font-size: 14px;
`

const SearchInput = styled.input`
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-size: 14px;
    color: #1E293B;
    background: #F8FAFC;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #DF0000;
        background: #FFFFFF;
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.1);
    }

    &::placeholder {
        color: #94A3B8;
    }
`

const SortButton = styled.button`
    padding: 10px 16px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: #F1F5F9;
        border-color: #CBD5E1;
    }

    &:active {
        transform: scale(0.98);
    }
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
`

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid #E2E8F0;
    border-top: 3px solid #DF0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`

const LoadingText = styled.div`
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
`

export default HomeworkClassView;
